
import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from "react-i18next";
import { WordColorStroopColor, WordColorStroopItem } from './WordColorStroopTypes';
import BorderLinearProgress from '../../../BorderLinearProgress';
import { useTheme } from '@mui/material/styles';

export const TIME_SPENT_PER_ITEM = 5.0;
const ITEM_COUNT = 15;


const getRandomColor = (): WordColorStroopColor => {
  const colors = Object.values(WordColorStroopColor);
  return colors[Math.floor(Math.random() * colors.length)];
};

const getRandomStimulus = (): string => {
  const stimuli = ['oooo', ...Object.values(WordColorStroopColor)];
  return stimuli[Math.floor(Math.random() * stimuli.length)];
};

export const WordColorStroopTask: React.FC<{ isRecording: boolean, cohort: string, onWordColorStroopChange: (data: any) => void }> = ({ isRecording, cohort, onWordColorStroopChange }) => {
  const { t } = useTranslation(cohort);
  const [items, setItems] = useState<WordColorStroopItem[]>([]);
  const [_timeFromRecordingStart, setTimeFromRecordingStart] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (isRecording) {
      setItems([]);
      setTimeFromRecordingStart(0);
    }
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeFromRecordingStart(prev => {
        const newTime = prev + 0.01;
        const flooredTime = Math.floor(newTime * 10) / 10.0;
        const itemsCount = items.length;

        if (newTime >= TIME_SPENT_PER_ITEM * itemsCount) {
          if (itemsCount < ITEM_COUNT) {
            let newItem: WordColorStroopItem = {
              color: getRandomColor(),
              stimulus: getRandomStimulus(),
              time: flooredTime
            };
            // Prevent duplicate color/stimulus as last item
            while (
              items.length > 0 &&
              items[items.length - 1].color === newItem.color &&
              items[items.length - 1].stimulus === newItem.stimulus) {
              newItem = {
                color: getRandomColor(),
                stimulus: getRandomStimulus(),
                time: flooredTime
              };
            }
            setItems(prevItems => [...prevItems, newItem]);
          }
        }
        return newTime;
      });
    }, 10); // 0.01 seconds = 10 ms

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, items.length]);

  useEffect(() => {
    onWordColorStroopChange(items);
  }, [items, onWordColorStroopChange]);

  const progress = (items.length / ITEM_COUNT) * 100;

  return (
     <>
      <Box sx={{ marginBottom: '1rem' }}>
        <BorderLinearProgress variant="determinate" value={progress} />
      </Box>
      <Typography sx={{ mb: 2 }}>{t('This exercise asks you to name out loud the color in which a word is displayed. Sometimes the word will be a color word: do not read the word aloud, just state the color in which it\'s displayed. For example, if you see the word "brown", and its letters are displayed in blue, you will say the word "blue".')}</Typography>
      <Typography sx={{ mb: 2 }}>{t('Please answer as quickly as possible.')}</Typography>
      <Typography sx={{ mb: 2 }}>{t('Time limit = 5 seconds/item, 15 random words. Total time 75s.')}</Typography>
      <Typography sx={{ mb: 2 }}>{t('Tap on the red circle below to start recording. The recording will continue through all tasks and automatically stop at the end.')}</Typography>
      <Box
        sx={{
          borderRadius: 2,
          p: 2.5,
          background: isDark ? 'black' : 'white',
        }}
      >
        <Typography
          variant="h3"
          component='div'
          align="center"
          sx={{ padding: 3, color: items.length > 0 ? items[items.length - 1].color : undefined }}
        >
          {items.length > 0 ? t(items[items.length - 1].stimulus) : ''}
        </Typography>
      </Box>
     </>
  );
};


